import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout';
import { ShowIf } from '../components/ShowIf';
import { ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../ApplicationInsights';
import ValidationGroup from 'devextreme-react/validation-group';
import { useValidateAndSaveApplication } from '../hooks/useValidateAndSaveApplication';
import { PropertyForm } from './PropertyForm';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation';
import { useRedirectIfComplete } from '../hooks/useRedirectIfApplicationComplete';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication';
import { ConfirmV2 } from '@apply/ui';
import { ProgressSpinner } from '@apply/components';
import { propertyDeleteHelper } from '../utils/propertyDeleteHelper';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { useAddPropertyToCache } from './queries/useAddPropertyToCache';
import { useDeleteProperty } from '../Review/queries/useDeleteProperty';
import { useWaitOverlayStore } from '@apply/components';
import { usePropertyGuidsCache } from './queries/usePropertyGuidsCache';
import { useLoanTypeCache } from '../../queries/useLoanTypeCache';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

export const Property: React.FC = () => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { search } = useLocation();
    const token = new URLSearchParams(search).get('token');
    const propertyGuidParameter = new URLSearchParams(search).get('pid');

    const { isPending } = useApplicationQuery(entryGuid, token);

    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    const propertyGuids = usePropertyGuidsCache();
    const propertyGuid = useMemo(() => {
        if (propertyGuidParameter) return propertyGuidParameter;
        if (propertyGuids.length > 0) return propertyGuids[propertyGuids.length - 1];
        return '';
    }, [propertyGuidParameter, propertyGuids]);
    useRedirectIfComplete();

    const validationGroupRef = useRef(null);
    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();

    const addPropertyToCache = useAddPropertyToCache();
    const deleteProperty = useDeleteProperty();
    const navigate = useNavigate();

    const [currentPropertyId, set_currentPropertyId] = useState<string | null>(null);
    const [disableEditFormOptionBudget, set_disableEditBudget] = useState(true);
    const [disableEditFormOptionSpecSheet, set_disableEditSpec] = useState(true);
    const [isConfirmDeletePropertyOpen, set_isConfirmDeletePropertyOpen] = useState(false);
    const [isLoading, set_isLoading] = useState(false);

    const { navigateApplicationForward, navigateApplicationBackward } = useNavigateApplication({
        page: CurrentApplicationPage.Property,
        entryGuid,
        propertyGuid,
    });

    const { updatePausePoint, clearPausePoint } = usePausePointTracker();

    const loanType = useLoanTypeCache();

    // When propertyGuid (from URL) is not yet in the cache, add a default property stub
    useEffect(() => {
        if (!propertyGuid) return;
        set_currentPropertyId(propertyGuid);

        const alreadyInCache = propertyGuids.includes(propertyGuid);
        if (!alreadyInCache) {
            addPropertyToCache(propertyGuid);
        }
    }, [propertyGuid]);

    const applyApi = new ApplyApi(applicationInsights);

    useEffect(() => {
        if (!loanType) return;
        const loadBudgetAndSpecSheets = async () => {
            const response = await applyApi.hasSavedForms(loanType);
            if (response.statusCode !== 200) return;
            const { hasSavedBudgets, savedSpecsheets } = response.result;
            set_disableEditBudget(!hasSavedBudgets);
            set_disableEditSpec(!savedSpecsheets);
        };
        loadBudgetAndSpecSheets();
    }, [loanType]);

    if (isPending && entryGuid) {
        return null;
    }

    const onConfirmDelete = async () => {
        if (propertyGuids.length <= 1) {
            set_isConfirmDeletePropertyOpen(false);
            return;
        }

        const { newCurrentPropertyId } = propertyDeleteHelper(propertyGuids, currentPropertyId, propertyGuid);
        set_currentPropertyId(newCurrentPropertyId ? newCurrentPropertyId : null);

        deleteProperty(propertyGuid);

        set_isLoading(true);
        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) {
            set_isLoading(false);
            return;
        }
        set_isLoading(false);
        set_isConfirmDeletePropertyOpen(false);

        navigateApplicationForward();
    };

    const onCancelDelete = () => {
        set_isConfirmDeletePropertyOpen(false);
        return;
    };

    const onSaveAndReview = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const response = await validateAndSaveApplication();

        if (!response) return;

        navigateApplicationForward();
    };

    const onSaveAndExit = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        updatePausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) return;

        navigate('/home');
    };

    const deletePropertyClick = (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        if (!propertyGuid || propertyGuids.length <= 1) {
            return;
        }

        set_isConfirmDeletePropertyOpen(true);
    };

    const previousStepButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await saveApplicationNoValidation();

        if (!applicationResponse) return;

        navigateApplicationBackward();
    };

    const nextStepButton = {
        id: 'saveAndContinueApplicationButton',
        dataTestid: 'saveAndContinueApplicationButton',
        children: 'Save and Continue',
        handleClick: onSaveAndReview,
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: 'Save and Exit',
        handleClick: onSaveAndExit,
    };

    const deletePropertyButton =
        propertyGuids.length <= 1
            ? undefined
            : {
                  id: 'deletePropertyButton',
                  dataTestid: 'deletePropertyButton',
                  children: 'Delete Property',
                  handleClick: deletePropertyClick,
              };

    return (
        <>
            {isLoading && <ProgressSpinner />}
            <ValidationGroup ref={validationGroupRef}>
                <ApplicationEntryLayout
                    currentCrumb={ApplicationCrumbType.Property}
                    saveAndExitButtonProps={saveAndExitButton}
                    nextStepButtonProps={nextStepButton}
                    deletePropertyButtonProps={deletePropertyButton}
                    previousStepButtonVisible={true}
                    previousStepButtonClick={previousStepButtonClick}
                >
                    <ShowIf showIf={!!propertyGuid}>
                        <div style={{ paddingLeft: '20px' }}>
                            <div
                                className='pageHeader'
                                style={{ marginTop: '.75rem', marginBottom: '0.75rem' }}
                            >
                                Property
                            </div>
                            {propertyGuid && (
                                <PropertyForm
                                    key={propertyGuid}
                                    propertyGuid={propertyGuid}
                                    disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                                    isReview={false}
                                />
                            )}
                        </div>
                    </ShowIf>
                </ApplicationEntryLayout>
            </ValidationGroup>
            <ConfirmV2
                id={`confirmDeletePropertyModal_${propertyGuid}`}
                open={isConfirmDeletePropertyOpen}
                title='Delete Property'
                onConfirm={onConfirmDelete}
                onCancel={onCancelDelete}
                okLabel='Yes'
                cancelLabel='No'
                width='340px'
                height='200px'
            >
                {`Are you sure you want to delete this property?`}
            </ConfirmV2>
        </>
    );
};
